import React, { useContext, useState, useEffect, useMemo } from 'react'
import { ViewTypeContext, EDITOR_ROUTE_ENUM } from 'components/Campaign'
import { STEP_TYPE_ENUM, TASK_TYPE_ENUM } from 'services/api/constants'
import { useFormikContext } from 'formik'
import { useNewTranslation } from 'hooks'
import ExtraTextEditor from '../../ExtraTextEditor'
import Field from 'components/Shared/Fields'
import { BaseEditorContainer, EditorContentCardContainer } from '../../styles'
import { PredefinedContext } from '../../index'
import { LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import Radio from 'components/Shared/Inputs/Radio'
import BaseDivider from '../../BaseDivider'
import Text from 'particles/Text'
import { Section, Title } from '../../../Shared'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { FormikTextListGroup } from 'particles/TextInputs/TextList/Group'
import { usePermission } from 'hooks/permissions'
import { CampaignManagement } from 'components/Shared/Permission/enums'

interface PostAndSubmitUrlEditorProps {
    type: TASK_TYPE_ENUM
    stepNumber: number
    uuid: string
    stepType: STEP_TYPE_ENUM
    requiredFieldNames: string[]
    isReadOnly?: boolean
}

const PostAndSubmitUrlEditor: React.FC<PostAndSubmitUrlEditorProps> = ({
    type,
    stepNumber,
    requiredFieldNames,
    uuid,
    stepType,
    isReadOnly,
}) => {
    const [t] = useNewTranslation()
    const [openAddLinkInBio, setOpenAddLinkInBio] = useState(false)
    const viewType = useContext(ViewTypeContext)
    const [linkInBioType, setLinkInBioType] = useState<LINK_IN_BIO_TYPE_ENUM>(LINK_IN_BIO_TYPE_ENUM.NO_LINK)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const submitNamePrefix = `stepData.${stepType}`
    const { isCampaignLive, linkInBioPostStepType, task } = useContext(PredefinedContext)
    const [videoCodeFieldDisable, setVideoCodeFieldDisable] = useState(
        // video code only exist in step Post in Tiktok
        // support [0] index, since brief requirement only have one step data
        // [1] step is the default step for campaign editor video code
        // return undefined if both not exist
        task?.steps[0]?.data?.video_code_field_disable ?? task?.steps[1]?.data?.video_code_field_disable,
    )
    const isEditableDefaultTag = usePermission(CampaignManagement.EditDefaultTags)

    const handleAddLinkInBio = (isToggled: boolean): void => {
        setOpenAddLinkInBio(isToggled)
        if (isToggled) {
            setFieldValue(`data.link_in_bio_type` as never, LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK)
            setFieldValue(`${submitNamePrefix}.data.link_in_bio_type` as never, LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK)
            setLinkInBioType(LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK)
        }
        if (!isToggled) {
            setFieldValue(`data.link_in_bio_type` as never, null)
            setFieldValue(`${submitNamePrefix}.data.link_in_bio_type` as never, null)
            setFieldValue(`${submitNamePrefix}.data.link_in_bio_url` as never, null)
            setFieldValue(`${submitNamePrefix}.data.link_in_bio_duration` as never, null)
        }
    }

    const handleToggleVideoCode = (isToggled: boolean): void => {
        setVideoCodeFieldDisable(!isToggled)
        setFieldValue(`${submitNamePrefix}.data.video_code_field_disable` as never, !isToggled)
    }

    useEffect(() => {
        if (task && task.data && !task.data.link_in_bio_type && openAddLinkInBio) {
            setOpenAddLinkInBio(false)
        } else {
            if (task && task.data) {
                const mLinkInBioType = task.data.link_in_bio_type
                if (
                    mLinkInBioType === LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK ||
                    mLinkInBioType === LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK
                ) {
                    setOpenAddLinkInBio(true)
                    setLinkInBioType(mLinkInBioType)
                }
            }
        }
    }, [task])

    const { stepTitlePlaceholderText, profileTagPlaceholderText } = useMemo(() => {
        let stepTitlePlaceholderKey = '',
            profileTagPlaceholderKey = ''
        switch (type) {
            case TASK_TYPE_ENUM.IG_POST:
            case TASK_TYPE_ENUM.IG_POST_V02:
                stepTitlePlaceholderKey = 'campaign.step.publishInstagramPostPostAndUrl'
                profileTagPlaceholderKey = 'campaignEditor.createTask.addInstagramPostProfileTagSubtext'
                break
            case TASK_TYPE_ENUM.IG_STORY:
            case TASK_TYPE_ENUM.IG_STORY_V02:
                stepTitlePlaceholderKey = 'campaign.step.publishInstagramPost'
                break
            case TASK_TYPE_ENUM.IG_REEL:
                stepTitlePlaceholderKey = 'campaign.step.publishInstagramPostPostAndUrl'
                profileTagPlaceholderKey = 'campaignEditor.createTask.addInstagramReelProfileTagSubtext'
                break
            case TASK_TYPE_ENUM.FB_POST:
            case TASK_TYPE_ENUM.FB_POST_V02:
                stepTitlePlaceholderKey = 'campaign.step.publishFacebookPostAndUrl'
                profileTagPlaceholderKey = 'campaignEditor.createTask.addFacebookProfileTagSubtext'
                break
            case TASK_TYPE_ENUM.TIKTOK_POST:
                stepTitlePlaceholderKey = 'campaign.step.postTikTokUrl'
                profileTagPlaceholderKey = 'campaignEditor.createTask.addTikTokProfileTagSubtext'
                break
            case TASK_TYPE_ENUM.XIAOHONGSHU_POST:
                stepTitlePlaceholderKey = 'campaign.step.postXiaohongshuUrl'
                profileTagPlaceholderKey = 'campaignEditor.createTask.XHSAddRequiredProfileTagSubtext'
                break
        }
        return {
            stepTitlePlaceholderText: t(stepTitlePlaceholderKey),
            profileTagPlaceholderText: t(profileTagPlaceholderKey),
        }
    }, [t, type])

    return (
        <BaseEditorContainer id={uuid && uuid}>
            <EditorContentCardContainer>
                <Title taskType={type} stepType={stepType} stepNumber={stepNumber} />
                <CampaignEditorInputFieldContainer>
                    <Field.TextInputField
                        className={'input-area'}
                        label={t('campaignEditor.createTask.enterStepTitleHeader')}
                        placeholder={stepTitlePlaceholderText}
                        name={`${submitNamePrefix}.data.title`}
                        isRequired={requiredFieldNames.includes('title')}
                        width={'100%'}
                        identifier={`${uuid}_${fieldIdentifiers.stepTitle}`}
                        isReadOnly={isReadOnly}
                    />
                </CampaignEditorInputFieldContainer>
                <BaseDivider />
                <Section
                    title={t('campaignEditor.createTask.enterSubmissionRequirementHeader')}
                    subtitle={t('campaignEditor.createTask.enterSubmissionRequirementSubtext')}
                >
                    <CampaignEditorInputFieldContainer>
                        <Field.TextAreaField
                            label={t('campaignEditor.createTask.enterDescriptionHeader')}
                            name={`${submitNamePrefix}.data.requirement_text`}
                            isRequired
                            width={'100%'}
                            minTextAreaHeight={76}
                            identifier={`${uuid}_${fieldIdentifiers.stepDescription}`}
                            isReadOnly={isReadOnly}
                        />
                    </CampaignEditorInputFieldContainer>
                </Section>
                {TASK_TYPE_ENUM.IG_STORY !== type ? (
                    <Section title={t('campaignEditor.createTask.addProfileTagHeader')}>
                        <CampaignEditorInputFieldContainer>
                            <FormikTextListGroup
                                title={t('campaignEditor.createTask.addRequiredProfileTagHeader')}
                                subtitle={profileTagPlaceholderText}
                                buttonText={t('button.addProfile')}
                                isRequired={requiredFieldNames.includes('media_requirement_tags')}
                                name={`${submitNamePrefix}.data.media_requirement_tags`}
                                width={'100%'}
                                identifier={`${uuid}_${fieldIdentifiers.profileTags}`}
                                isReadOnly={isReadOnly}
                                inputIcon="at"
                                isEditableDefaultTag={isEditableDefaultTag}
                            />
                        </CampaignEditorInputFieldContainer>
                    </Section>
                ) : null}
                {type === TASK_TYPE_ENUM.TIKTOK_POST && (
                    <>
                        <CampaignEditorInputFieldContainer>
                            <Toggle
                                isChecked={!videoCodeFieldDisable}
                                handleChange={() => {
                                    const updateValue = !Boolean(!videoCodeFieldDisable)
                                    handleToggleVideoCode(updateValue)
                                }}
                                isDisabled={viewType === EDITOR_ROUTE_ENUM.VIEW}
                                label={t('submission.table.videoCodeLabel')}
                                subtext={t('campaignEditor.createTask.turnOnVideoCodeSubtext')}
                                identifier={`${uuid}_${fieldIdentifiers.videoCodeToggle}`}
                                isReadOnly={isReadOnly}
                            />
                        </CampaignEditorInputFieldContainer>

                        <BaseDivider />
                    </>
                )}
                {[
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_POST_URL,
                    STEP_TYPE_ENUM.POST_TO_IG_POST,
                    STEP_TYPE_ENUM.POST_TO_IG_STORY,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_TIKTOK_POST_URL,
                    STEP_TYPE_ENUM.POST_AND_SUBMIT_PROOF_INSTAGRAM_REEL_URL,
                ].includes(stepType) ? (
                    <div className="toggle-block">
                        <CampaignEditorInputFieldContainer>
                            <Toggle
                                isChecked={openAddLinkInBio === true}
                                isDisabled={viewType === EDITOR_ROUTE_ENUM.VIEW}
                                isReadOnly={isReadOnly || isCampaignLive || undefined}
                                handleChange={() => {
                                    const updateValue = !Boolean(openAddLinkInBio)
                                    handleAddLinkInBio(updateValue)
                                }}
                                label={t('campaignEditor.createTask.turnOnLinkInBioHeader')}
                                subtext={t('campaignEditor.createTask.turnOnLinkInBioSubtext')}
                                identifier={`${uuid}_${fieldIdentifiers.linkInBioToggle}`}
                            />
                        </CampaignEditorInputFieldContainer>

                        <BaseDivider />
                        {openAddLinkInBio && linkInBioPostStepType && (
                            <>
                                <div className="link-extend-group">
                                    <CampaignEditorInputFieldContainer>
                                        <Field.NumberInputField
                                            title={t('campaignEditor.createTask.enterLinkInBioDurationHeader')}
                                            subtitle={t('campaignEditor.createTask.enterLinkInBioDurationSubtext')}
                                            placeholder="1"
                                            width="133px"
                                            name={`${submitNamePrefix}.data.link_in_bio_duration`}
                                            isRequired
                                            isReadOnly={isReadOnly}
                                        />
                                    </CampaignEditorInputFieldContainer>

                                    {/* Hide if read only & radio not selected */}
                                    {isReadOnly && linkInBioType !== LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK ? null : (
                                        <div className="link-action-group">
                                            <CampaignEditorInputFieldContainer>
                                                <Radio
                                                    name="link_in_bio_type"
                                                    isCheck={linkInBioType === LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK}
                                                    onChange={(): void => {
                                                        if (task && task.data) {
                                                            task.data.link_in_bio_type =
                                                                LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK
                                                            setFieldValue(
                                                                `data.link_in_bio_type` as never,
                                                                LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK,
                                                            )
                                                            //sync the step data
                                                            setFieldValue(
                                                                `${submitNamePrefix}.data.link_in_bio_type` as never,
                                                                LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK,
                                                            )
                                                            setFieldTouched(
                                                                `${submitNamePrefix}.data.link_in_bio_url` as never,
                                                                true,
                                                            )
                                                            setLinkInBioType(LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK)
                                                        }
                                                    }}
                                                    isDisable={isCampaignLive}
                                                    isReadOnly={isReadOnly || isCampaignLive || undefined}
                                                />

                                                <Field.TextInputField
                                                    className={'input-area'}
                                                    label={t('campaignEditor.createTask.enterStaticLinknBioHeader')}
                                                    placeholder={t('placeholderText.websiteExample')}
                                                    size="m"
                                                    name={`${submitNamePrefix}.data.link_in_bio_url`}
                                                    width="100%"
                                                    isDisabled={
                                                        isCampaignLive &&
                                                        linkInBioType !== LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK
                                                    }
                                                    isReadOnly={isReadOnly || isCampaignLive || undefined}
                                                />
                                            </CampaignEditorInputFieldContainer>
                                        </div>
                                    )}

                                    {/* Hide if read only & radio not selected */}
                                    {isReadOnly && linkInBioType !== LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK ? null : (
                                        <div className="link-action-group">
                                            <CampaignEditorInputFieldContainer>
                                                <Radio
                                                    name="link_in_bio_type"
                                                    isCheck={linkInBioType === LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK}
                                                    onChange={(): void => {
                                                        setLinkInBioType(LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK)
                                                        if (task && task.data) {
                                                            task.data.link_in_bio_type =
                                                                LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK
                                                            setFieldValue(
                                                                `data.link_in_bio_type` as never,
                                                                LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK,
                                                            )
                                                            //sync the step data for the
                                                            setFieldValue(
                                                                `${submitNamePrefix}.data.link_in_bio_type` as never,
                                                                LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK,
                                                            )
                                                            setFieldTouched(
                                                                `${submitNamePrefix}.data.link_in_bio_url` as never,
                                                                false,
                                                            )
                                                            setFieldValue(
                                                                `${submitNamePrefix}.data.link_in_bio_url` as never,
                                                                '',
                                                            )
                                                        }
                                                    }}
                                                    isDisable={isCampaignLive}
                                                    isReadOnly={isReadOnly || isCampaignLive || undefined}
                                                />
                                            </CampaignEditorInputFieldContainer>
                                            <div>
                                                <Text scale="bodyM500">
                                                    {t('campaignEditor.createTask.enterUniqueLinkInBioHeader')}
                                                </Text>
                                                <Text color="secondary">
                                                    {t('campaignEditor.createTask.enterLinkInBioDurationSubtext')}
                                                </Text>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <BaseDivider />
                            </>
                        )}
                    </div>
                ) : null}
                {!isReadOnly && (
                    <CampaignEditorInputFieldContainer>
                        <ExtraTextEditor name={`${submitNamePrefix}.data.extra_data`} isRequired />
                    </CampaignEditorInputFieldContainer>
                )}
            </EditorContentCardContainer>
        </BaseEditorContainer>
    )
}
export default PostAndSubmitUrlEditor
